/**
 * MiniPlayer - Compact Audio Player for Device Header
 * 
 * Features:
 * - Shows current playback (radio station, track name)
 * - Play/Pause toggle
 * - Stop button
 * - Only visible when audio is active
 * 
 * Uses existing AudioPlayerContext - NO new audio logic.
 */
import React from 'react';
import { useAudioPlayer } from '../../contexts/AudioPlayerContext';
import { Play, Pause, Square, Radio } from 'lucide-react';

export default function MiniPlayer() {
  const { 
    isPlaying, 
    isLoading,
    currentStation, 
    isPaused,
    pause, 
    resume, 
    stop 
  } = useAudioPlayer();

  // Only show when there's an active station
  const hasActiveStation = currentStation != null;
  
  if (!hasActiveStation) {
    return null;
  }

  const stationName = currentStation?.name || 'Unbekannt';
  const isActive = isPlaying || isPaused;

  const handlePlayPause = () => {
    if (isPlaying) {
      pause();
    } else {
      resume();
    }
  };

  const handleStop = () => {
    stop();
  };

  return (
    <div className="mini-player">
      {/* Glassmorphism Container */}
      <div className="bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-2xl shadow-lg px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Station Icon */}
          <div className="shrink-0">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Radio className="w-5 h-5 text-white" />
            </div>
          </div>

          {/* Station Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {stationName}
            </p>
            <p className="text-xs text-gray-500">
              {isLoading ? 'Lädt...' : isPlaying ? 'Spielt jetzt' : 'Pausiert'}
            </p>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Play/Pause Button */}
            <button
              onClick={handlePlayPause}
              disabled={isLoading}
              className="w-12 h-12 rounded-full bg-violet-600 hover:bg-violet-700 active:bg-violet-800 disabled:bg-gray-400 flex items-center justify-center transition-colors shadow-md"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <Pause className="w-5 h-5 text-white" />
              ) : (
                <Play className="w-5 h-5 text-white ml-0.5" />
              )}
            </button>

            {/* Stop Button */}
            <button
              onClick={handleStop}
              className="w-12 h-12 rounded-full bg-gray-200 hover:bg-gray-300 active:bg-gray-400 flex items-center justify-center transition-colors"
              aria-label="Stop"
            >
              <Square className="w-4 h-4 text-gray-700 fill-current" />
            </button>
          </div>
        </div>

        {/* Loading Indicator */}
        {isLoading && (
          <div className="mt-2">
            <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-violet-600 animate-pulse" style={{ width: '60%' }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
